import { useRouter } from "next/router";

import { PencilIcon, TrashIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import useModalState from "@logics/hooks/useModalState";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { useDeleteEventMutation } from "@domains/event/network/eventMutations";
import { useFetchEventDetailQuery } from "@domains/event/network/eventQueries";
import { GA_EVENT_IDS } from "@domains/common/constants/googleAnalyticsIds";
import ConfirmModal from "@domains/common/views/modal/ConfirmModal/ConfirmModal";
import WebViewBridge from "@logics/utils/webViewBridge";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";
import { useFetchCategoryQuery } from "@domains/common/networks/category/categoryQueries";
import NoticeDialog from "@domains/common/views/dialog/CommonDialog/NoticeDialog";
import NoticeModal from "@domains/common/views/modal/NoticeModal/NoticeModal";

import * as S from "./HostMenuBottomSheet.style";

interface HostMenuBottomSheetProps {
  eventId: string;
  isEventClosed: boolean;
}

const HostMenuBottomSheet = ({
  eventId,
  isEventClosed,
}: HostMenuBottomSheetProps) => {
  const router = useRouter();

  const { openModal, closeModal } = useModalState();

  const { data: category } = useFetchCategoryQuery();
  const { data: eventDetail } = useFetchEventDetailQuery(eventId);
  const eventDeleteMutation = useDeleteEventMutation();

  const eventInfoData = eventDetail
    ? {
        evenId: eventDetail.id,
        eventCategory: category[eventDetail.categories[0]],
        eventTitle: eventDetail.title,
        eventImageNum: eventDetail.imageUrlList.length,
        eventFee: eventDetail.attendanceFee,
        eventDateTime: eventDetail.date,
        eventMaxParticipantCount: eventDetail.maxParticipantCount,
        eventParticipantCount: eventDetail.participantCount,
      }
    : {};

  const handleClickEditEvent = () => {
    if (isEventClosed) {
      openModal({
        CustomComponent: (
          <NoticeDialog
            modal={{
              title: "Gathering date passed",
            }}
            confirmButton={{
              textColor: grayScale.white,
              onClick: closeModal,
            }}
          >
            <S.DialogContent name="s">
              The gathering you are trying to modify has already taken place.
              Please create a new gathering.
            </S.DialogContent>
          </NoticeDialog>
        ),
      });

      return;
    }

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToEditEvent(eventId);
    } else {
      router.push({
        pathname: NAVIGATION_ROUTE.EDIT_EVENT.PATHNAME,
        query: { eventId },
      });
    }
  };

  const handleClickDeleteEvent = async () => {
    if (!eventId || !eventDetail) return;

    try {
      await eventDeleteMutation.mutateAsync(eventId);

      dataTrackEvent({
        eventName:
          DATA_TRACKING_EVENT_NAME.HOST_EVENT.CLICK_FINISH_EVENT_DELETE,
        properties: eventInfoData,
      });

      if (WebViewBridge.isInAppWebView()) {
        WebViewBridge.getInstance().navigateBack();
        return;
      }

      router.back();

      closeModal();
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenDeleteConfirmModal = () => {
    if (!eventId || !eventDetail) return;

    // NOTE: 참가자가 한 명만 있는 경우는 참가자가 없고 호스트만 있는 것이므로 삭제 가능.
    if (eventDetail?.participantCount > 1) {
      openModal({
        CustomComponent: (
          <NoticeModal
            modal={{
              title: "Unable to delete the gathering.",
              description:
                "Unable to delete the gathering as there are participants.",
            }}
            ctaButton={{
              onClickConfirm: closeModal,
            }}
          />
        ),
      });

      return;
    }

    dataTrackEvent({
      eventName: DATA_TRACKING_EVENT_NAME.HOST_EVENT.CLICK_START_EVENT_DELETE,
      properties: eventInfoData,
    });

    openModal({
      CustomComponent: (
        <ConfirmModal
          modalTitle="Confirm Gathering Deletion"
          modalDescription="Are you sure you want to delete the gathering?"
          cancelButton={{
            onClickCancel: closeModal,
          }}
          confirmButton={{
            buttonText: "Delete",
            id: GA_EVENT_IDS.EVENT.HOST_FINISH_DELETE,
            onClickConfirm: handleClickDeleteEvent,
          }}
        />
      ),
    });
  };

  return (
    <S.BottomSheetContainer
      setting={{ $withBackdropFilter: true, $noPadding: true }}
    >
      <S.MenuList role="button" onClick={handleClickEditEvent}>
        <PencilIcon strokeColor={grayScale.white} />
        <span>Edit gathering</span>
      </S.MenuList>
      <S.MenuList
        role="button"
        id={GA_EVENT_IDS.EVENT.HOST_START_DELETE}
        onClick={handleOpenDeleteConfirmModal}
      >
        <TrashIcon strokeColor={grayScale.white} />
        <span>Delete gathering</span>
      </S.MenuList>
    </S.BottomSheetContainer>
  );
};

export default HostMenuBottomSheet;
